const automaticTourClaimedInThisTab = new Set<string>()
const seenInThisTab = new Map<string, Set<string>>()

const STORAGE_VERSION = 1

// The previous implementation stored one key for the whole release. Preserve
// that knowledge so adding the first per-feature slide does not replay the
// original five slides for existing users.
const LEGACY_RELEASES = [
  {
    signature: 'v1:structured-ingest,schema-agent,mobile-forensics,osint-agent,structured-search',
    featureIds: [
      'structured-ingest',
      'schema-agent',
      'mobile-forensics',
      'osint-agent',
      'structured-search',
    ],
  },
] as const

interface SeenFeaturesPayload {
  version: number
  featureIds: string[]
}

/** Claim the unseen slides for this user's one automatic tour in the current tab. */
export function claimUnseenWhatsNewFeatures(
  userId: string,
  availableFeatureIds: readonly string[]
): string[] {
  if (typeof window === 'undefined' || !userId) return []
  const userKey = encodeURIComponent(userId)
  if (automaticTourClaimedInThisTab.has(userKey)) return []
  automaticTourClaimedInThisTab.add(userKey)

  const seen = readSeenFeatures(userId)
  return Array.from(new Set(availableFeatureIds)).filter(id => !seen.has(id))
}

/** Persist one slide only after it has actually been displayed. */
export function markWhatsNewFeatureSeen(userId: string, featureId: string): void {
  if (typeof window === 'undefined' || !userId || !featureId) return
  const seen = readSeenFeatures(userId)
  seen.add(featureId)
  seenInThisTab.set(userId, seen)
  try {
    const payload: SeenFeaturesPayload = {
      version: STORAGE_VERSION,
      featureIds: Array.from(seen).sort(),
    }
    window.localStorage.setItem(storageKey(userId), JSON.stringify(payload))
  } catch {
    // Full or disabled storage falls back to the in-memory set for this tab.
  }
}

function readSeenFeatures(userId: string): Set<string> {
  const seen = new Set(seenInThisTab.get(userId) ?? [])
  try {
    const stored = window.localStorage.getItem(storageKey(userId))
    const parsed = parseSeenFeatures(stored)
    if (parsed) {
      for (const id of parsed) seen.add(id)
    } else {
      migrateLegacyReleases(userId, seen)
    }
  } catch {
    // Reading storage is optional; the in-memory fallback remains authoritative.
  }
  seenInThisTab.set(userId, seen)
  return seen
}

function migrateLegacyReleases(userId: string, seen: Set<string>): void {
  let migrated = false
  for (const release of LEGACY_RELEASES) {
    if (!window.localStorage.getItem(legacyStorageKey(userId, release.signature))) continue
    for (const id of release.featureIds) seen.add(id)
    migrated = true
  }
  if (!migrated) return
  const payload: SeenFeaturesPayload = {
    version: STORAGE_VERSION,
    featureIds: Array.from(seen).sort(),
  }
  window.localStorage.setItem(storageKey(userId), JSON.stringify(payload))
}

function parseSeenFeatures(raw: string | null): string[] | null {
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object') return null
    const featureIds = (value as Record<string, unknown>).featureIds
    if (!Array.isArray(featureIds) || !featureIds.every(id => typeof id === 'string')) return null
    return featureIds
  } catch {
    return null
  }
}

function storageKey(userId: string): string {
  return `spectre:whats-new:${encodeURIComponent(userId)}:seen-features`
}

function legacyStorageKey(userId: string, release: string): string {
  return `spectre:whats-new:${encodeURIComponent(userId)}:${release}`
}
